import { FC, useMemo, useRef } from 'react';

import { CKEditor } from '@ckeditor/ckeditor5-react';
import {
  ClassicEditor,
  Bold,
  Essentials,
  Heading,
  Italic,
  Mention,
  TextTransformation,
  Underline,
  Link,
} from 'ckeditor5';

const LINK_PATTERN =
  /(https?:\/\/[^\s<]+|www\.[^\s<]+|[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,})/gi;

const createHref = (value: string) => {
  if (value.startsWith('http://') || value.startsWith('https://')) {
    return value;
  }

  if (value.includes('@')) {
    return `mailto:${value}`;
  }

  return `https://${value}`;
};

const setLinkAttributes = (link: HTMLAnchorElement) => {
  const href = link.getAttribute('href') ?? '';

  if (!href || href.startsWith('mailto:')) {
    return;
  }

  link.target = '_blank';
  link.rel = 'noopener noreferrer';
};

const linkifyText = (content: string) => {
  if (!content || typeof window === 'undefined') {
    return content;
  }

  const parser = new DOMParser();
  const document = parser.parseFromString(content, 'text/html');
  const links = document.querySelectorAll<HTMLAnchorElement>('a[href]');

  links.forEach((link) => {
    setLinkAttributes(link);
  });

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT);
  const textNodes: Text[] = [];

  let currentNode = walker.nextNode();
  while (currentNode) {
    textNodes.push(currentNode as Text);
    currentNode = walker.nextNode();
  }

  textNodes.forEach((textNode) => {
    const parentElement = textNode.parentElement;
    const nodeValue = textNode.nodeValue ?? '';

    if (
      !parentElement ||
      parentElement.closest('a') ||
      !LINK_PATTERN.test(nodeValue)
    ) {
      LINK_PATTERN.lastIndex = 0;
      return;
    }

    LINK_PATTERN.lastIndex = 0;
    const fragment = document.createDocumentFragment();
    let lastIndex = 0;

    nodeValue.replace(LINK_PATTERN, (match, _value, offset: number) => {
      if (offset > lastIndex) {
        fragment.appendChild(
          document.createTextNode(nodeValue.slice(lastIndex, offset)),
        );
      }

      const link = document.createElement('a');
      const href = createHref(match);
      link.href = href;
      link.className = 'rich-text-link';
      setLinkAttributes(link);
      link.textContent = match;
      fragment.appendChild(link);

      lastIndex = offset + match.length;
      return match;
    });

    if (lastIndex < nodeValue.length) {
      fragment.appendChild(document.createTextNode(nodeValue.slice(lastIndex)));
    }

    textNode.parentNode?.replaceChild(fragment, textNode);
  });

  return document.body.innerHTML;
};

const RichTextDisplay: FC<{ text?: string; enableMention?: boolean }> = ({
  text = '',
  enableMention = false,
}) => {
  const editorRef = useRef<any | null>(null);
  const formattedText = useMemo(() => linkifyText(text), [text]);

  return (
    <div className="rich-text-readonly">
      <CKEditor
        ref={editorRef}
        editor={ClassicEditor}
        data={formattedText}
        config={{
          licenseKey: 'GPL',
          toolbar: [],
          plugins: [
            Bold,
            Essentials,
            Heading,
            Italic,
            ...(enableMention ? [Mention] : []),
            TextTransformation,
            Underline,
            Link,
          ],
          link: {
            addTargetToExternalLinks: true,
            defaultProtocol: 'https://',
          },
        }}
        onReady={(editor) => {
          editorRef.current = editor;
          editor.enableReadOnlyMode('my-feature-id');

          const editableElement = editor.ui?.getEditableElement?.();
          if (editableElement) {
            editableElement.setAttribute('tabindex', '-1');
          }
        }}
      />
    </div>
  );
};

export default RichTextDisplay;
